import apolloLogger from 'apollo-link-logger';
import type { RequestHandler } from '@apollo/client';
import { ApolloLink, HttpLink } from '@apollo/client';
import { CONFIG } from '@frameio/config';
import { auth } from '@frameio/utils/auth';
import { isUXPEnvironment } from '@frameio/utils/config';
import { isServer } from '@frameio/utils/config/helpers';
import { getCookie } from '@frameio/utils/cookies';
import { session } from '@frameio/utils/session';
import { hasSubscription } from '../../utils/hasSubscription';
import type { ApolloClientOpts } from '../index';
import createAbsintheSocketLink from './createAbsintheSocketLink';
import HeaderInfoLink from './HeaderInfoLink';
import {
  AuthLink,
  AuthRefreshLink,
  CustomMutationErrorLink,
  ErrorLink,
  PreviewEnvLink,
  shareReviewAuthLink,
  shareUnidentifiedUserLink,
  SubscriptionRetryLink,
  uploadAccessTokenLink,
  uploadAuthLink,
  uploadLinkUnavailableLink,
  UXPHeaderInfoLink,
  UXPLink,
  UXPRefreshLink,
  warningsLink,
} from './index';
import { TelemetryLink } from './TelemetryLink';

export function getLinks({
  serverDetails,
  isUXPIntegration,
  avoidAuthRedirect,
}: ApolloClientOpts): Array<ApolloLink | RequestHandler> {
  // An array of ApolloLinks that we will add to before passing to the ApolloClient
  // The conditionals below should ideally be handled with ApolloLink.split()
  // but in beta, this has been a challenge to get working
  const links: Array<ApolloLink | RequestHandler> = [];

  /** add apollo logging if in dev  */
  if (!isServer && process.env.NODE_ENV === 'development') {
    links.push(warningsLink);
    if (CONFIG.enableApolloLogger) links.push(apolloLogger);
  }

  /**
   * Only the anonymous share review and public upload experiences should bypass
   * redirects to the auth portal — all other app pages should redirect. The
   * explicit `avoidAuthRedirect` opt covers anonymous share pages that have no
   * token, such as the share-unavailable (not-found) page.
   */
  auth.avoidRedirectToAuthPortal =
    Boolean(serverDetails?.tokens?.share) ||
    Boolean(serverDetails?.tokens?.upload) ||
    Boolean(avoidAuthRedirect);

  /**
   * If user is viewing a share review page, then add the share review auth
   */
  if (serverDetails?.tokens?.share && !isServer) {
    links.push(shareReviewAuthLink(serverDetails.tokens.share));
    links.push(shareUnidentifiedUserLink(serverDetails.tokens.share));
  }

  /**
   * The public upload link page is treated as fully anonymous: no user access
   * token is attached, even for logged-in visitors. The upload-auth link is the
   * only credential (it carries the link token + passphrase, once entered).
   */
  const isUploadLinkSession = !!serverDetails?.tokens?.upload;
  if (isUploadLinkSession && !isServer) {
    links.push(uploadAuthLink(serverDetails!.tokens.upload!));
    // Inject the verified uploader's access token (once OTP-verified) on every
    // upload-session request. Clearing a stale token is handled at the upload
    // attempt (UploadQueue), not here, so unrelated anonymous UNAUTHENTICATED
    // responses (e.g. the Me query) can't wipe a valid session.
    links.push(uploadAccessTokenLink);
    // A PERMISSION_DENIED on any upload-link request means the link was disabled
    // or expired mid-session; reload so the RSC layout renders the unavailable
    // page. Scoped here (not in the global ErrorLink) since PERMISSION_DENIED is
    // a generic code the main app must not reload on.
    links.push(uploadLinkUnavailableLink);
  }

  const isUXP = isUXPIntegration && isUXPEnvironment();
  if (!isServer && !isUXP && !isUploadLinkSession) {
    links.push(AuthRefreshLink());
  }

  // For UXP integrations we need a dedicated refresh link
  if (!isServer && isUXP) {
    links.push(UXPRefreshLink(), UXPHeaderInfoLink);
  }

  // AuthLink attaches the user's `Authorization: Bearer` header. Skip it for
  // the upload link session so those requests stay unauthenticated.
  links.push(HeaderInfoLink);
  if (!isUploadLinkSession) {
    links.push(AuthLink);
  }
  links.push(ErrorLink, CustomMutationErrorLink);

  if (CONFIG.enablePreviewEnvironmentBanner) {
    links.push(PreviewEnvLink);
  }

  if (CONFIG.thirdParty.telemetryOptions?.traceGraphqlQueries) {
    links.push(TelemetryLink());
  }

  links.push(SubscriptionRetryLink);
  const previewEnvironment = !isServer
    ? getCookie('x-preview-environment')
    : undefined;

  const httpLink = new HttpLink({
    uri: serverDetails?.uri || CONFIG.graphqlUrl,
    credentials: 'same-origin',
    headers: {
      ...session.getSessionHeaders(),
      ...(previewEnvironment && {
        'x-preview-environment': previewEnvironment,
      }),
    },
  });

  // The Websocket object is unavailable serverside.
  // Only add AbsintheSocketLink in the browser.
  if (isServer) {
    links.push(httpLink);
    if (isUXPIntegration) {
      throw new Error('UXP integration is not supported on the server');
    }
  } else {
    const websocketLink = createAbsintheSocketLink();
    const uxpLink = isUXPIntegration ? UXPLink() : httpLink;

    /**
     * Use WebsocketLink for subscriptions, HttpLink for queries and mutations
     * normally. For UXP Integrations all GraphQL operations should be routed to
     * the UXPLink.
     */
    const splitLink = ApolloLink.split(
      (operation) =>
        hasSubscription(operation.query) &&
        !operation.getContext().resolveWithUXP,
      websocketLink,
      ApolloLink.split(
        (operation) =>
          isUXPIntegration && operation.getContext().resolveWithUXP,
        uxpLink!,
        httpLink
      )
    );

    links.push(splitLink);
  }

  return links;
}
